import { useEffect, useRef, useState } from 'react';
import { api, getAccessToken } from '../lib/api';

/* 图片缩略图：带鉴权的懒加载 + 内存缓存 */
const cache = new Map<string, string>();

export function FileThumb({ nodeId, alt, className }: { nodeId: string; alt?: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(cache.get(nodeId) ?? null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  // 进入视口才加载
  useEffect(() => {
    const el = ref.current;
    if (!el || url) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [url]);

  useEffect(() => {
    if (!visible || url) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(api.nodes.downloadUrl(nodeId), {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (!res.ok) { setFailed(true); return; }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        // 限制缓存规模（最多 200 张）
        if (cache.size > 200) {
          const firstKey = cache.keys().next().value;
          if (firstKey) cache.delete(firstKey);
        }
        cache.set(nodeId, objectUrl);
        if (alive) setUrl(objectUrl);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
  }, [visible, nodeId, url]);

  if (failed) return null;

  return (
    <div ref={ref} className={`relative overflow-hidden ${className || ''}`}>
      {url ? (
        <img src={url} alt={alt || ''} loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full skeleton" />
      )}
    </div>
  );
}
